import { useEffect, useState, type FormEvent } from "react";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import { Eyebrow, HeadingItalic } from "../../components/design";
import {
  adminListAdmins,
  adminAddAdmin,
  adminRemoveAdmin,
  type AdminAccount,
} from "../../lib/api";

export default function AdminAdmins() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [currentAdmin, setCurrentAdmin] = useState<string>("");
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const r = await adminListAdmins();
      setAdmins(r.admins || []);
      setCurrentAdmin(r.current_admin || "");
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    const email = newEmail.trim().toLowerCase();
    if (!email) {
      setAddError("Bitte E-Mail angeben.");
      return;
    }
    setAdding(true);
    try {
      await adminAddAdmin(email, newNote.trim());
      setNewEmail("");
      setNewNote("");
      await reload();
    } catch (e: any) {
      setAddError(e?.message || "Konnte Admin nicht hinzufügen.");
    } finally {
      setAdding(false);
    }
  }

  async function onRemove(email: string) {
    const ok = window.confirm(
      `Admin-Zugang für ${email} wirklich entfernen?\n\nDer Zugang erlischt sofort, beim nächsten /admin-Zugriff.`,
    );
    if (!ok) return;
    setRemovingEmail(email);
    try {
      await adminRemoveAdmin(email);
      await reload();
    } catch (e: any) {
      alert(e?.message || "Konnte Admin nicht entfernen.");
    } finally {
      setRemovingEmail(null);
    }
  }

  if (loading) return <FullPageLoading label="Lade Admins …" />;

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />
      <main
        className="desk-container-admin"
        style={{ padding: "36px 22px 60px" }}
      >
        <Eyebrow>Admin-Verwaltung</Eyebrow>
        <HeadingItalic size={34}>
          Wer darf <em>hier hinein</em>?
        </HeadingItalic>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 15,
            color: "var(--ink-soft)",
            margin: "8px 0 28px",
            maxWidth: 600,
          }}
        >
          Bootstrap-Admins (Quelle: <code>ADMIN_EMAILS</code>) sind im
          Deploy-Secret hinterlegt und können hier nicht entfernt werden.
          Zusätzliche Admins werden in Firestore verwaltet und können jederzeit
          revoked werden.
        </p>

        {error && (
          <p
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(107,32,24,0.06)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderRadius: 8,
              color: "var(--oxblood)",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              marginBottom: 24,
            }}
          >
            {error}
          </p>
        )}

        {/* Add admin form */}
        <section
          className="card"
          style={{ padding: "20px 22px", marginBottom: 32 }}
        >
          <h2
            className="t-display-it"
            style={{ margin: "0 0 12px", fontSize: 20 }}
          >
            Admin hinzufügen
          </h2>
          <form
            onSubmit={onAdd}
            style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr" }}
          >
            <div>
              <label
                className="t-caps"
                htmlFor="ad-email"
                style={{ display: "block", marginBottom: 6 }}
              >
                E-Mail
              </label>
              <input
                id="ad-email"
                type="email"
                className="input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="vorname.nachname@firma.de"
                required
              />
            </div>
            <div>
              <label
                className="t-caps"
                htmlFor="ad-note"
                style={{ display: "block", marginBottom: 6 }}
              >
                Notiz (optional)
              </label>
              <input
                id="ad-note"
                type="text"
                className="input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="z. B. Geschäftsführer / temporärer Test-Zugang"
              />
            </div>
            {addError && (
              <p
                role="alert"
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--oxblood)",
                  margin: 0,
                }}
              >
                {addError}
              </p>
            )}
            <div>
              <button
                type="submit"
                disabled={adding}
                className="btn-primary"
                style={{ padding: "12px 22px", minHeight: 0, fontSize: 14 }}
              >
                {adding ? <InlineSpinner /> : "Admin hinzufügen"}
              </button>
            </div>
          </form>
        </section>

        {/* Admin list */}
        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 22px",
              borderBottom: "1px solid rgba(43,26,15,0.10)",
            }}
          >
            <h2
              className="t-display-it"
              style={{ margin: 0, fontSize: 20 }}
            >
              Aktuelle Admins ({admins.length})
            </h2>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "var(--f-body)",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "var(--paper-200)",
                    fontFamily: "var(--f-ui)",
                  }}
                >
                  <Th>E-Mail</Th>
                  <Th>Quelle</Th>
                  <Th>Hinzugefügt von</Th>
                  <Th>Wann</Th>
                  <Th>Notiz</Th>
                  <Th style={{ textAlign: "right" }}>Aktion</Th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => {
                  const isSelf = a.email === currentAdmin;
                  const isEnv = a.source === "env";
                  return (
                    <tr
                      key={a.email}
                      style={{
                        borderBottom: "1px solid rgba(43,26,15,0.06)",
                      }}
                    >
                      <Td>
                        <span
                          style={{
                            fontFamily: "var(--f-display)",
                            fontStyle: "italic",
                            fontSize: 16,
                            color: "var(--ink)",
                          }}
                        >
                          {a.email}
                        </span>
                        {isSelf && (
                          <span
                            className="t-caps"
                            style={{
                              marginLeft: 8,
                              color: "var(--sage)",
                            }}
                          >
                            · Sie
                          </span>
                        )}
                      </Td>
                      <Td>
                        <SourceBadge source={a.source} />
                      </Td>
                      <Td>
                        {a.added_by ? (
                          <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                            {a.added_by}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-faint)" }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {a.added_at ? (
                          <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                            {formatDateTime(a.added_at)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-faint)" }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {a.note ? (
                          <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                            {a.note}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-faint)" }}>—</span>
                        )}
                      </Td>
                      <Td style={{ textAlign: "right" }}>
                        {isEnv || isSelf ? (
                          <span
                            className="t-caps"
                            style={{ color: "var(--ink-faint)" }}
                          >
                            {isEnv ? "geschützt" : "Sie selbst"}
                          </span>
                        ) : (
                          <button
                            onClick={() => onRemove(a.email)}
                            disabled={removingEmail === a.email}
                            className="btn-secondary"
                            style={{
                              padding: "6px 12px",
                              minHeight: 0,
                              fontSize: 13,
                              color: "var(--oxblood)",
                              borderColor: "var(--oxblood)",
                            }}
                          >
                            {removingEmail === a.email ? (
                              <InlineSpinner />
                            ) : (
                              "Entfernen"
                            )}
                          </button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function Th({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      className="t-caps"
      style={{
        textAlign: "left",
        padding: "10px 16px",
        fontWeight: 600,
        color: "var(--ink-faint)",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "12px 16px",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function SourceBadge({ source }: { source: "env" | "firestore" }) {
  if (source === "env") {
    return (
      <span
        className="t-caps"
        style={{
          display: "inline-block",
          padding: "3px 8px",
          background: "rgba(168,122,58,0.18)",
          color: "var(--ochre)",
          borderRadius: 999,
        }}
      >
        Bootstrap
      </span>
    );
  }
  return (
    <span
      className="t-caps"
      style={{
        display: "inline-block",
        padding: "3px 8px",
        background: "rgba(95,107,79,0.18)",
        color: "var(--sage)",
        borderRadius: 999,
      }}
    >
      Verwaltet
    </span>
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
